// app/components/VehicleModal.tsx
import { Portal, Modal, Text, Button, TextInput, useTheme, Surface } from 'react-native-paper';
import { SegmentedButtons } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn, SubmitHandler } from 'react-hook-form';
import { VehicleFormData } from '../lib/types';
import { FUEL_TYPE_OPTIONS, SCREEN_WIDTH } from '../lib/constants';
import { ScrollView, Platform } from 'react-native';
import FuelTypeSelector from './FuelTypeSelector';

interface VehicleModalProps {
  visible: boolean;
  onDismiss: () => void;
  onLogout?: () => void; // Manter para compatibilidade, mas deprecado
  form: UseFormReturn<VehicleFormData>;
  onSubmit: (data: VehicleFormData, form: UseFormReturn<VehicleFormData>) => Promise<boolean>;
  localRegNumber: string;
  setLocalRegNumber: (value: string) => void;
  formatRegistrationNumber: (input: string) => void;
  isEditing?: boolean;
}

export const VehicleModal = ({ visible, onDismiss, onLogout, form, onSubmit, localRegNumber, setLocalRegNumber, formatRegistrationNumber, isEditing = false }: VehicleModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { control, handleSubmit, formState: { errors, isSubmitting } } = form;

  return (
    <Portal>
      <Modal visible={visible} dismissable={false} contentContainerStyle={[
        { backgroundColor: theme.colors.surface, padding: 20, margin: 20, borderRadius: 8 },
        Platform.OS === 'web' ? {
          maxWidth: 500,
          width: '90%',
          alignSelf: 'center',
        } : {}
      ]}>
        <Text variant="headlineSmall" style={{ marginBottom: 20 }}>
          {isEditing ? t('editVehicleInfo') : t('addVehicleInfo')}
        </Text>
        
        {/* Show warning message when editing */}
        {isEditing && (
          <Surface
            style={{
              padding: 12,
              marginBottom: 16,
              borderRadius: 8,
              backgroundColor: theme.colors.secondaryContainer,
            }}
          >
            <Text variant="bodySmall" style={{ color: theme.colors.onSecondaryContainer }}>
              {t('vehicleModal.registrationNumberImmutable')}
            </Text>
          </Surface>
        )}
        
        <Controller
          control={control}
          name="registration_number"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <TextInput
                label={t('registrationNumber')}
                mode="outlined"
                onChangeText={(text) => {
                  if (!isEditing) {
                    const formatted = formatRegistrationNumber(text) ?? text;
                    setLocalRegNumber(formatted);
                    onChange(formatted);
                  }
                }}
                value={localRegNumber || value}
                error={!!error}
                style={{ marginBottom: 10 }}
                placeholder="XX-XX-XX (e.g., 12-AB-34)"
                maxLength={8}
                disabled={isEditing}
                editable={!isEditing}
              />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Controller
          control={control}
          name="fuel_type"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <FuelTypeSelector value={value} onChange={onChange} style={{ marginBottom: 10 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Controller
          control={control}
          name="make"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <TextInput label={t('make')} mode="outlined" onChangeText={onChange} value={value} error={!!error} style={{ marginBottom: 10 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Controller
          control={control}
          name="model"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <TextInput label={t('model')} mode="outlined" onChangeText={onChange} value={value} error={!!error} style={{ marginBottom: 10 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Controller
          control={control}
          name="year"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <TextInput label={t('year')} mode="outlined" onChangeText={(text) => onChange(parseInt(text) || value)} value={value.toString()} error={!!error} keyboardType="numeric" style={{ marginBottom: 20 }} />
              {error && <Text style={{ color: theme.colors.error }}>{error.message}</Text>}
            </>
          )}
        />
        <Button mode="contained" onPress={handleSubmit((data) => onSubmit(data, form))} loading={isSubmitting} disabled={isSubmitting}>
          {t('submitButton')}
        </Button>
        <Button mode="outlined" onPress={onDismiss} style={{ marginTop: 10 }}>
          {t('cancel')}
        </Button>
      </Modal>
    </Portal>
  );
};